import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Card,
  Chip,
  InputBase,
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { useCartStore } from '../../stores/cartStore';
import { getImageUrl } from '../../types/product';
import { palette } from '../../theme/colors';
import { useCartTax } from '../../hooks/useTaxRate';
import { formatTaxRatePercent } from '../../config/pricing';

export const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState('');

  const items = useCartStore((state) => state.items);
  // Per-item, per-organization: a cart may legitimately span organizations on
  // different rates, so there is not necessarily one rate for the whole cart.
  const { amount: tax, uniformRate } = useCartTax(items);
  const removeItem = useCartStore((state) => state.removeItem);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const clearCart = useCartStore((state) => state.clearCart);
  const getTotal = useCartStore((state) => state.getTotal());
  const discountCode = useCartStore((state) => state.discountCode);
  const discountRate = useCartStore((state) => state.discountRate);
  const applyDiscountCode = useCartStore((state) => state.applyDiscountCode);
  const clearDiscount = useCartStore((state) => state.clearDiscount);

  const isEmpty = items.length === 0;

  const subtotal = getTotal;
  const hasDiscount = discountCode !== null;
  const discount = hasDiscount ? subtotal * discountRate : 0;
  const total = subtotal + tax - discount;

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const handleApplyPromo = () => {
    applyDiscountCode(promoCode.toLowerCase());
  };

  if (isEmpty) {
    return (
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Card
          sx={{
            p: 8,
            textAlign: 'center',
            borderRadius: 4,
          }}
        >
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              bgcolor: palette.sky,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <ShoppingBagOutlinedIcon sx={{ fontSize: 48, color: palette.textMuted }} />
          </Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Your cart is empty
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Start shopping to add items to your cart
          </Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/products"
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{
              bgcolor: palette.primary,
              color: palette.textOnPrimary,
              '&:hover': { bgcolor: palette.primaryDark },
            }}
          >
            Browse Products
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 400px' },
          gap: 4,
        }}
      >
        {/* Left Side - Products Grid (same as products page style) */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              Cart Items ({items.length})
            </Typography>
            <Button
              variant="text"
              startIcon={<RestartAltOutlinedIcon />}
              onClick={clearCart}
              sx={{ color: palette.textSecondary }}
            >
              Clear All
            </Button>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {items.map((item) => (
              <Card
                key={item.productId}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}
              >
                {/* Quantity Badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    zIndex: 2,
                    bgcolor: palette.primary,
                    color: palette.textOnPrimary,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 50,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                  }}
                >
                  x{item.quantity}
                </Box>

                {/* Delete Button */}
                <IconButton
                  onClick={() => removeItem(item.productId)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    bgcolor: palette.errorLight,
                    color: palette.error,
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: palette.error, color: palette.surface },
                  }}
                >
                  <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>

                {/* Product Image */}
                <Box
                  sx={{
                    height: 160,
                    bgcolor: palette.sky,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '16px 16px 0 0',
                    overflow: 'hidden',
                  }}
                >
                  {item.image ? (
                    <Box
                      component="img"
                      src={getImageUrl(item.image)}
                      alt={item.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        p: 2,
                      }}
                    />
                  ) : (
                    <Inventory2OutlinedIcon sx={{ fontSize: 48, color: palette.textMuted }} />
                  )}
                </Box>

                {/* Product Info */}
                <Box sx={{ p: 2, flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} noWrap>
                    {item.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {item.locationName}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700} color={palette.textPrimary}>
                    {(item.price * item.quantity).toFixed(2)} DZD
                  </Typography>
                </Box>

                {/* Quantity Controls */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    p: 2,
                    pt: 0,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                    sx={{
                      bgcolor: palette.sky,
                      '&:hover': { bgcolor: palette.border },
                    }}
                  >
                    <RemoveOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Typography
                    sx={{
                      minWidth: 40,
                      textAlign: 'center',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                    }}
                  >
                    {String(item.quantity).padStart(2, '0')}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                    sx={{
                      bgcolor: palette.primary,
                      color: palette.textOnPrimary,
                      '&:hover': { bgcolor: palette.primaryDark },
                    }}
                  >
                    <AddOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Right Side - Transaction Panel */}
        <Card
          sx={{
            p: 3,
            borderRadius: 3,
            height: 'fit-content',
            position: 'sticky',
            top: 100,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 3,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Detail Transaction
            </Typography>
            <Button
              variant="text"
              startIcon={<DeleteOutlineOutlinedIcon />}
              onClick={clearCart}
              sx={{
                color: palette.error,
                fontSize: '0.8rem',
                '&:hover': { bgcolor: palette.errorLight },
              }}
            >
              Reset Order
            </Button>
          </Box>

          {/* Cart Items Summary */}
          <Box sx={{ mb: 3 }}>
            {items.slice(0, 3).map((item) => (
              <Box
                key={item.productId}
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 2,
                  pb: 2,
                  borderBottom: `1px solid ${palette.border}`,
                }}
              >
                {/* Item Image */}
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    bgcolor: palette.sky,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  {item.image ? (
                    <Box
                      component="img"
                      src={getImageUrl(item.image)}
                      alt={item.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.5 }}
                    />
                  ) : (
                    <Inventory2OutlinedIcon sx={{ fontSize: 24, color: palette.textMuted }} />
                  )}
                </Box>

                {/* Item Details */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={600} noWrap>
                    {item.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip
                      label={`x${item.quantity}`}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        bgcolor: palette.sky,
                      }}
                    />
                  </Box>
                </Box>

                {/* Price */}
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {(item.price * item.quantity).toFixed(2)} DZD
                  </Typography>
                </Box>
              </Box>
            ))}

            {items.length > 3 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                +{items.length - 3} more items
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Promo Code */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <LocalOfferOutlinedIcon sx={{ fontSize: 18, color: palette.textSecondary }} />
              <Typography variant="body2" fontWeight={500}>
                {hasDiscount ? 'Promo New User (10%)' : 'Add Promo Code'}
              </Typography>
              {hasDiscount && (
                <Button
                  size="small"
                  onClick={clearDiscount}
                  sx={{
                    ml: 'auto',
                    fontSize: '0.7rem',
                    color: palette.primary,
                    bgcolor: `${palette.primary}20`,
                    borderRadius: 50,
                    px: 1.5,
                    '&:hover': { bgcolor: `${palette.primary}30` },
                  }}
                >
                  Change Promo
                </Button>
              )}
            </Box>
            {!hasDiscount && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                }}
              >
                <InputBase
                  placeholder="Enter code (try: NEW10)"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  sx={{
                    flex: 1,
                    px: 2,
                    py: 1,
                    bgcolor: palette.sky,
                    borderRadius: 2,
                    fontSize: '0.85rem',
                  }}
                />
                <Button
                  onClick={handleApplyPromo}
                  sx={{
                    bgcolor: palette.sidebarBg,
                    color: palette.surface,
                    borderRadius: 2,
                    px: 2,
                    '&:hover': { bgcolor: palette.sidebarHover },
                  }}
                >
                  Apply
                </Button>
              </Box>
            )}
          </Box>

          {/* Order Summary */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Sub-Total
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {subtotal.toFixed(2)} DZD
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {uniformRate !== null ? `Tax (${formatTaxRatePercent(uniformRate)}%)` : 'Tax'}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {tax.toFixed(2)} DZD
              </Typography>
            </Box>
            {hasDiscount && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1.5,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Discount
                </Typography>
                <Typography variant="body2" fontWeight={600} color={palette.success}>
                  -{discount.toFixed(2)} DZD
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Total */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 3,
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              Total Payment
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {total.toFixed(2)} DZD
            </Typography>
          </Box>

          {/* Payment Method */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: palette.sky,
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 24,
                  borderRadius: 1,
                  bgcolor: palette.accent,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CreditCardOutlinedIcon sx={{ fontSize: 16, color: palette.textOnAccent }} />
              </Box>
              <Typography variant="body2" fontWeight={500}>
                Reserve & Pay at Store
              </Typography>
            </Box>
          </Box>

          {/* Checkout Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleCheckout}
            sx={{
              bgcolor: palette.accent,
              color: palette.textOnAccent,
              py: 1.75,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: 50,
              '&:hover': { bgcolor: palette.accentDark },
            }}
          >
            Continue
          </Button>
        </Card>
      </Box>
    </Box>
  );
};
